import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader.jsx';
import PageState from '../../components/PageState.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import Notice from '../../components/Notice.jsx';
import LifecycleNotice from '../../components/LifecycleNotice.jsx';
import ExternalLink from '../../components/ExternalLink.jsx';
import { Dot } from '../../components/StatusWord.jsx';
import KpiCard, { KpiGrid } from '../../components/KpiCard.jsx';
import { api } from '../../lib/api.js';
import { lifecycleNotice } from '../../lib/lifecycle-notice.js';
import { customerSectionPath } from '../../lib/navigation.js';
import { longDate, plural, shortDate } from '../../lib/access-review-format.js';
import { EMAIL_BUDGET, FOOTER_LINE } from '../../../../shared/access-reviews.mjs';
import { collectionPhrase } from '../security/SecurityPage.jsx';
import { SOURCES_ID } from '../data-collection/SourcesCard.jsx';
import CurrentCampaign, { CURRENT_ID } from './CurrentCampaign.jsx';
import FindingsCard from './FindingsCard.jsx';
import NotesCard, { NOTES_ID } from './NotesCard.jsx';
import CampaignsTable, { reportPath } from './CampaignsTable.jsx';
import SetupDialog from './SetupDialog.jsx';
import ReviewWorkspace from './ReviewWorkspace.jsx';
import { signedInAdapter } from './my-review-adapter.js';
import { isWorkspaceView } from './workspace-groups.js';

// The embedded review workspace: the overdue notice's "Review the N" lands here.
const WORKSPACE_ID = 'access-reviews-workspace';

export const TITLE = 'Access reviews';
const SUBLINE = 'Privileged role assignments in Microsoft Entra: who holds them, who vouched for them, and which removals are done.';

/** The signed-in reviewer's own items, inside the portal (spec 5: "Your reviews (6)" at the top of the page). */
export const mineRoute = (customerId) => `${customerSectionPath(customerId, 'access-reviews')}/mine`;

/**
 * Access reviews (spec docs/superpowers/specs/2026-10-26-access-reviews-design.md, section 13.1). Top to bottom: the
 * header (schedule in the subline, Start or Send reminder, Setup), notices with the whole story and one action, four
 * KPIs, "Your reviews", the current campaign with removals to make, findings between campaigns, notes, campaigns and
 * the footer line. Every state of 13.3 has its own rendering. The API decides names and actions (`access`); the viewer
 * and the operator get counts only.
 *
 * @param {object} props
 * @param {boolean} [props.readOnly] the viewer role: counts only, no controls
 * @param {boolean} [props.staffReadOnly] IPC Global staff on a customer below the top tier: sees, never acts
 */
export default function AccessReviewsPage({ customerId, customer, audience, onRefresh, readOnly = false, staffReadOnly = false, marketplaceListingUrl = '' }) {
  const [load, setLoad] = useState({ status: 'loading', data: null, error: null });
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    api.getAccessReviews(customerId)
      .then((data) => { if (!cancelled) setLoad({ status: 'ready', data, error: null }); })
      .catch((e) => { if (!cancelled) setLoad((prev) => ({ status: prev.data ? 'ready' : 'error', data: prev.data, error: e })); });
    return () => { cancelled = true; };
  }, [customerId, reloads]);

  const data = load.data;
  const hasData = Boolean(data && data.state === 'ready') || Boolean(customer?.lastRunAt);
  const notice = customer ? lifecycleNotice(customer, { audience, hasData }) : null;

  if (notice && !hasData) {
    return (
      <div>
        <PageHeader title={TITLE} subline={SUBLINE} />
        <LifecycleNotice customer={customer} audience={audience} hasData={hasData} onRefresh={onRefresh} readOnly={readOnly} />
      </div>
    );
  }
  if (load.status === 'loading') {
    return <><PageHeader title={TITLE} subline={SUBLINE} /><PageState state="loading" header={false} cards={4} label="Loading access reviews" /></>;
  }
  if (load.status === 'error') {
    return (
      <>
        <PageHeader title={TITLE} subline={SUBLINE} />
        <PageState state="error" title="Access reviews could not be loaded" message={`${load.error?.message ?? 'The request failed'}. Check your connection, then retry.`} onRetry={reload} />
      </>
    );
  }
  const access = data.access ?? {};
  const blocked = readOnly || staffReadOnly;
  const can = {
    edit: access.canEdit === true && !blocked,
    close: access.canClose === true && !blocked,
    names: access.names === true && !readOnly,
  };
  if (data.state !== 'ready') return <NotReady data={data} customerId={customerId} can={can} />;
  return (
    <Ready
      data={data} customerId={customerId} can={can} readOnly={readOnly} staffReadOnly={staffReadOnly} customerName={customer?.companyName ?? null}
      marketplaceListingUrl={marketplaceListingUrl} onChanged={reload}
    />
  );
}

/** No permission, source off, first collection pending (spec 13.3): one full-story notice and what the page will show. */
function NotReady({ data, customerId, can }) {
  const sourcesTo = `${customerSectionPath(customerId, 'data-collection')}#${SOURCES_ID}`;
  const askAdmin = <span className="text-[13px] text-ipc-muted">Your onGuard admin can do this.</span>;
  const willShow = (
    <SectionCard title="What this page will show" testId="access-reviews-will-show">
      <ul className="list-disc space-y-1 pl-5 text-sm text-ipc-ink">
        <li>Who holds each privileged Microsoft Entra role, with the signals that matter: guests, no recent sign-in, standing access.</li>
        <li>A review campaign: a named person keeps or removes each assignment, with a reason, by a due date.</li>
        <li>The removals to make, verified on the next collection, and locked evidence for your audits.</li>
      </ul>
    </SectionCard>
  );
  let body;
  if (data.state === 'no_permission') {
    body = (
      <Notice
        tone="warn" title="Access reviews needs permission to read admin role assignments" testId="access-reviews-no-permission"
        action={can.edit ? <Link className="btn-primary min-h-[40px]" to={sourcesTo}>Add the permission</Link> : askAdmin}
      >
        onGuard reads who holds which Microsoft Entra role with the Directory.Read.All permission, read only. Without it this
        page cannot list the assignments to review. Add the permission in Data collection and a Global Administrator approves
        it once in Microsoft.
      </Notice>
    );
  } else if (data.state === 'source_off') {
    body = (
      <Notice
        tone="warn" title="Admin role collection is off" testId="access-reviews-source-off"
        action={can.edit ? <Link className="btn-primary min-h-[40px]" to={sourcesTo}>Turn it on</Link> : askAdmin}
      >
        The Admin roles source is turned off in Data collection, so onGuard has no role assignments to review. It needs no new
        permission. Turn it on and the next collection fills this page.
      </Notice>
    );
  } else {
    body = (
      <SectionCard title="Your first role assignment collection is on its way" testId="access-reviews-pending">
        <p className="text-sm text-ipc-ink">It arrives with the next collection. Nothing to do: onGuard reads the admin role assignments with the permissions you already granted.</p>
      </SectionCard>
    );
  }
  return (
    <div className="space-y-4">
      <PageHeader title={TITLE} subline={SUBLINE} />
      {body}
      {willShow}
      <p className="text-[13px] text-ipc-muted" data-testid="access-reviews-footer">{FOOTER_LINE}</p>
    </div>
  );
}

/** A start answer in words: 201 open, 202 collecting, 409 with the reason (spec 6.2, 6.3). */
function startOutcome(e) {
  const body = e?.body ?? {};
  if (e?.status === 409 && body.error === 'no_valid_reviewer') {
    const n = Number(body.unassignable) || 0;
    return { tone: 'warn', title: 'No valid reviewer', message: `No valid reviewer for ${n === 1 ? '1 assignment' : `${n || 'some'} assignments`}. Check that at least one customer admin has an enabled account with a mailbox in your domain, then start again.` };
  }
  if (e?.status === 409 && body.error === 'nothing_to_review') return { tone: 'warn', title: 'Nothing to review', message: 'There is nothing to review: the last collector run found no privileged role assignments.' };
  if (e?.status === 409 && body.error === 'stale') return { tone: 'warn', title: 'The latest collection is too old to start a review', message: body.message ?? 'Collect now and start: the review starts when the collection lands.', collect: true };
  if (e?.status === 409 && body.error === 'too_soon') return { tone: 'info', title: 'A review was started recently', message: body.message ?? `onGuard starts a review by hand at most once every 30 days${body.nextAllowedAt ? `; the next one is possible on ${longDate(body.nextAllowedAt)}` : ''}.` };
  if (e?.status === 409 && body.error === 'campaign_open') return { tone: 'info', title: 'A review is already open', message: body.message ?? 'Finish or close the open review before starting another.' };
  if (e?.status === 403) return { tone: 'warn', title: 'The review was not started', message: body.message ?? e?.message ?? 'Your role cannot start a review.' };
  // onGuard has no verified domains from Microsoft yet: the server says so in full, nothing to add.
  if (body.error === 'verified_domains_unavailable') return { tone: 'warn', title: 'The review was not started', message: body.message ?? 'onGuard could not read your verified domains from Microsoft yet. Try again in a few minutes.' };
  return { tone: 'bad', title: 'The review was not started', message: `${body.message ?? e?.message ?? 'The request failed'}. Try again in a moment.` };
}

function Ready({ data, customerId, can, readOnly, staffReadOnly, customerName = null, marketplaceListingUrl, onChanged }) {
  const [setup, setSetup] = useState({ open: false, mode: 'edit', focusRole: null });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const setupOpener = useRef(null);
  const entitled = data.access?.entitled !== false;
  const current = data.current;
  const openCampaign = current && ['open', 'overdue'].includes(current.state);
  const activeCampaign = current && ['open', 'overdue', 'closing'].includes(current.state);
  const neverRan = entitled && !current && (data.campaigns ?? []).length === 0;

  // The admin reviews in the page: every item of the open campaign by person and by role (Access Reviews 1.1). The API
  // proves the admin; nobody else (viewer, operator, staff) asks for it. A failed load leaves the "Your reviews" link.
  const showWorkspace = can.edit && can.names && !readOnly && !staffReadOnly && entitled && openCampaign;
  const [workspace, setWorkspace] = useState(null);
  const workspaceRef = useRef(null);
  const campaignId = current?.campaignId ?? null;
  useEffect(() => {
    if (!showWorkspace) { setWorkspace(null); return undefined; }
    let cancelled = false;
    Promise.resolve()
      .then(() => api.getMyAccessReviews(customerId))
      .then((view) => { if (!cancelled) setWorkspace(isWorkspaceView(view) ? view : null); })
      .catch(() => { if (!cancelled) setWorkspace(null); });
    return () => { cancelled = true; };
  }, [customerId, campaignId, showWorkspace]);

  const openSetup = (mode = 'edit', focusRole = null) => setSetup({ open: true, mode, focusRole });

  // A first review opens Setup prefilled with the defaults, which starts the campaign in one more click (spec 13.3).
  const start = async ({ collectFirst = false, fromSetup = false } = {}) => {
    if (!collectFirst && !fromSetup && data.settings && (data.settings.version ?? 0) === 0) { openSetup('start'); return; }
    setBusy(true);
    setStatus(null);
    try {
      const out = await api.startAccessReview(customerId, collectFirst ? { collectFirst: true } : {});
      if (out?.state === 'collecting') setStatus({ tone: 'info', title: 'Collecting now', message: 'The review starts when the collection lands, usually within a few minutes. Nothing else to do.' });
      else setStatus({ tone: 'good', title: 'The review started', message: 'Each reviewer gets one email with their assignments. You can follow the progress below.' });
      onChanged();
    } catch (e) {
      const out = startOutcome(e);
      setStatus(out);
    } finally {
      setBusy(false);
    }
  };

  const remind = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const out = await api.remindAccessReview(customerId, current.campaignId);
      const sent = Number.isInteger(out?.sent) ? out.sent : 0;
      setStatus(sent > 0
        ? { tone: 'good', title: `Reminder sent to ${plural(sent, 'reviewer')}`, message: 'Only reviewers with undecided assignments get it.' }
        : remindNothingSent(out?.skipped));
    } catch (e) {
      setStatus({ tone: e?.status === 403 || e?.status === 429 ? 'warn' : 'bad', title: 'The reminder was not sent', message: e?.body?.message ?? e?.message ?? 'The request failed.' });
    } finally {
      setBusy(false);
    }
  };

  // The results report of a campaign closed for decisions (product rule 2026-10-30): available without waiting for the
  // removals, which are verified separately and end in a verification addendum.
  const createResults = async (campaignId) => {
    setBusy(true);
    setStatus(null);
    try {
      await api.createAccessReviewResultsReport(customerId, campaignId);
      setStatus({ tone: 'good', title: 'The results report is ready', message: 'Open it from Campaigns or Reports. The removals are verified separately; the verification addendum follows when they are verified or the removal window ends.' });
      onChanged();
    } catch (e) {
      setStatus({ tone: e?.status === 409 ? 'warn' : 'bad', title: 'The results report was not created', message: e?.body?.message ?? e?.message ?? 'The request failed. Try again in a moment.' });
    } finally {
      setBusy(false);
    }
  };

  const startLabel = data.stale ? 'Collect now and start' : 'Start review now';
  let action = null;
  if (can.edit && entitled) {
    if (openCampaign) {
      const why = remindWhy(current.remind);
      action = (
        <span className="flex flex-col items-start gap-1 sm:items-end">
          <button type="button" className="btn-primary min-h-[40px]" disabled={busy || why !== null} aria-describedby={why ? 'access-reviews-remind-why' : undefined} onClick={remind} data-testid="access-reviews-remind">Send reminder</button>
          {why ? <span id="access-reviews-remind-why" className="max-w-[26rem] text-[13px] text-ipc-muted sm:text-right" data-testid="access-reviews-remind-why">{why}</span> : null}
        </span>
      );
    }
    else if (!activeCampaign) action = <button type="button" className="btn-primary min-h-[40px]" disabled={busy} onClick={() => start({ collectFirst: data.stale === true })} data-testid="access-reviews-start">{startLabel}</button>;
  }
  const secondary = can.edit && entitled && data.settings
    ? <button ref={setupOpener} type="button" className="btn-secondary min-h-[40px]" onClick={() => openSetup('edit')} data-testid="access-reviews-setup">Setup</button>
    : null;

  // The way in to the caller's OWN items: the workspace in this page (scrolled to and focused), else their own review
  // page. Items waiting on other reviewers have their own "Review the N" in the workspace, so no duplicate here.
  const ownPending = entitled && data.yourReviews?.count > 0 && !staffReadOnly;
  const reviewNow = !ownPending ? null : workspace
    ? { onClick: () => { const el = workspaceRef.current; el?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }); el?.focus?.(); } }
    : { to: mineRoute(customerId) };
  const noticeAction = (n) => noticeActionFor(n, { customerId, can, data, openSetup, start, marketplaceListingUrl, workspaceShown: workspace !== null, createResults, busy, reportsLinked: !readOnly });

  return (
    <div className="space-y-4" data-testid="access-reviews-page">
      <PageHeader
        title={TITLE}
        subline={<Subline data={data} customerId={customerId} canEdit={can.edit && entitled} onSchedule={() => openSetup('edit')} readOnly={readOnly} />}
        action={action}
        secondary={secondary}
      />
      {/* Never next to Setup: whoever may edit is not read only (customer zero's own staff admins included). */}
      {!can.edit && (readOnly || (!can.names && !staffReadOnly)) ? (
        <p className="text-[13.5px] text-ipc-muted" data-testid="access-reviews-read-only">
          Read only: your role shows counts and progress. Names, decisions and setup are for your onGuard admin.
        </p>
      ) : null}
      {staffReadOnly ? (
        <Notice
          tone="info" testId="access-reviews-staff-notice"
          title={`You are signed in as IPC Global staff, so this review is read only here. To review, sign in with your ${customerName || 'company'} account.`}
        />
      ) : null}
      {data.namesUnavailable ? <p className="text-[13.5px] text-ipc-muted" data-testid="access-reviews-names-unavailable">The names could not be read from your AWS account just now; the counts are complete. Reload the page in a moment to see them.</p> : null}
      {status ? (
        <Notice tone={status.tone} title={status.title} testId="access-reviews-status" role={status.tone === 'bad' ? 'alert' : 'status'}
          action={status.collect && can.edit ? <button type="button" className="btn-primary min-h-[40px]" onClick={() => start({ collectFirst: true })}>Collect now and start</button> : null}
        >
          {status.message}
        </Notice>
      ) : null}
      {(data.notices ?? []).map((n) => (
        <Notice key={n.id} tone={n.tone === 'bad' ? 'bad' : n.tone === 'warn' ? 'warn' : 'info'} title={n.title} testId={`access-reviews-notice-${n.id}`} action={noticeAction(n)}>
          {n.body}
        </Notice>
      ))}

      {entitled && data.kpis ? <Kpis kpis={data.kpis} current={current} names={can.names} /> : null}
      {neverRan ? <NoCampaignYet data={data} can={can} onStart={() => start({ collectFirst: data.stale === true })} onSchedule={() => openSetup('edit')} busy={busy} startLabel={startLabel} /> : null}

      {workspace ? (
        <section id={WORKSPACE_ID} ref={workspaceRef} tabIndex={-1} aria-label="Review the assignments" className="scroll-mt-4 space-y-4 focus:outline-none" data-testid="access-reviews-workspace">
          <ReviewWorkspace key={workspace.campaign?.campaignId} view={workspace} adapter={signedInAdapter(customerId, workspace, { onRecorded: onChanged })} embedded />
        </section>
      ) : entitled && data.yourReviews?.count > 0 ? (
        <p className="flex flex-wrap items-center gap-x-3 text-sm text-ipc-ink" data-testid="access-reviews-yours">
          <Dot tone="info" />
          <span>You have {plural(data.yourReviews.count, 'assignment')} to review.</span>
          <Link className="font-semibold text-ipc-light hover:underline" to={mineRoute(customerId)}>Open your review</Link>
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {entitled && activeCampaign ? (
          <div className="lg:col-span-2">
            <CurrentCampaign current={current} kpis={data.kpis} customerId={customerId} can={can} onChanged={onChanged} reviewNow={reviewNow} />
          </div>
        ) : null}
        <div className={entitled && activeCampaign ? '' : 'lg:col-span-3'}>
          <FindingsCard findings={data.findings ?? []} notesShown={can.names && Array.isArray(data.notes)} />
        </div>
      </div>

      {can.names && Array.isArray(data.notes) ? (
        <NotesCard notes={data.notes} assignments={data.assignments ?? []} version={data.notesVersion ?? 0} customerId={customerId} canEdit={can.edit} onChanged={onChanged} />
      ) : null}

      {entitled && (data.campaigns ?? []).length > 0 ? <CampaignsTable campaigns={data.campaigns} customerId={customerId} reportsLinked={!readOnly} /> : null}

      <p className="text-[13px] text-ipc-muted" data-testid="access-reviews-footer">{FOOTER_LINE}</p>

      {can.edit && data.settings ? (
        <SetupDialog
          open={setup.open} mode={setup.mode} focusRole={setup.focusRole} onClose={() => setSetup((s) => ({ ...s, open: false }))}
          customerId={customerId} settings={data.settings} roles={data.roles ?? []} returnFocusRef={setupOpener} names={can.names}
          onSaved={async (mode) => { setSetup((s) => ({ ...s, open: false })); if (mode === 'start') await start({ fromSetup: true }); else onChanged(); }}
        />
      ) : null}
    </div>
  );
}

/** What to say when a reminder reached nobody, by the reason the API gave (never "all decided" unless that is true). */
export function remindNothingSent(skipped) {
  const n = (k) => (Number.isInteger(skipped?.[k]) ? skipped[k] : 0);
  const total = n('decided') + n('alreadyEmailedToday') + n('budget') + n('other');
  if (total > 0 && n('decided') === total) return { tone: 'info', title: 'No reminder needed', message: 'Every reviewer has decided their assignments.' };
  if (n('alreadyEmailedToday') > 0 && n('budget') + n('other') === 0) return { tone: 'info', title: 'No reminder sent', message: 'The reviewers got their email today; try again tomorrow.' };
  return { tone: 'info', title: 'No reminder sent', message: 'No reviewer could be emailed right now. A reviewer may have had an email today or used the emails allowed for this review. Nothing was used up, so you can try again later.' };
}

/**
 * Why "Send reminder" is disabled, in one visible line (Hugo Andrade's email budget, 2026-10-26), or null when it is
 * allowed. An answer without `remind` (an older API) leaves the button enabled; the API still decides.
 */
export function remindWhy(remind) {
  if (!remind || remind.allowed !== false) return null;
  if (remind.reason === 'recent') return `A reminder was sent recently. The next one is possible on ${longDate(remind.nextAllowedAt) || 'a later day'}.`;
  if (remind.reason === 'cap') return `Every reviewer with pending items already got a reminder. onGuard sends at most ${EMAIL_BUDGET.perReviewerPerCampaign} emails per reviewer in a review, and a final reminder goes out 2 days before the due date.`;
  if (remind.reason === 'nothing') return 'Every assignment has a decision.';
  return 'This review no longer takes reminders.';
}

/** One action per notice (spec 13.3), only for whoever may take it; reading actions (an anchor) for everyone. */
function noticeActionFor(n, { customerId, can, data, openSetup, start, marketplaceListingUrl, workspaceShown = false, createResults = null, busy = false, reportsLinked = true }) {
  const a = n.action;
  if (!a) return null;
  const sourcesTo = `${customerSectionPath(customerId, 'data-collection')}#${SOURCES_ID}`;
  switch (a.kind) {
    case 'open-entra':
      return can.names && a.href ? <ExternalLink href={a.href} className="btn-secondary min-h-[40px]">{a.label}</ExternalLink> : null;
    case 'review-overdue':
      // The admin decides any item in the workspace of this page (on behalf of the assigned reviewer).
      if (workspaceShown) return <a className="btn-primary min-h-[40px]" href={`#${WORKSPACE_ID}`}>{a.label}</a>;
      if (data.yourReviews?.count > 0) return <Link className="btn-primary min-h-[40px]" to={mineRoute(customerId)}>{a.label}</Link>;
      return <a className="btn-secondary min-h-[40px]" href={`#${CURRENT_ID}`}>{a.label}</a>;
    case 'setup':
      return can.edit ? <button type="button" className="btn-primary min-h-[40px]" onClick={() => openSetup('edit', data.current?.blockers?.[0]?.roleTemplateId ?? null)}>{a.label}</button> : null;
    case 'compare-tiers':
      return marketplaceListingUrl ? <ExternalLink href={marketplaceListingUrl} className="btn-secondary min-h-[40px]">{a.label}</ExternalLink> : null;
    case 'permissions':
    case 'turn-on':
      return can.edit ? <Link className="btn-primary min-h-[40px]" to={sourcesTo}>{a.label}</Link> : null;
    case 'collect':
      return can.edit ? <button type="button" className="btn-primary min-h-[40px]" onClick={() => start({ collectFirst: true })}>{a.label}</button> : null;
    case 'notes':
      return can.names ? <a className="btn-secondary min-h-[40px]" href={`#${NOTES_ID}`}>{a.label}</a> : null;
    case 'create-results-report':
      return can.edit && createResults && a.campaignId ? <button type="button" className="btn-primary min-h-[40px]" disabled={busy} onClick={() => createResults(a.campaignId)} data-testid="access-reviews-create-results">{a.label}</button> : null;
    case 'open-report':
      return reportsLinked && a.reportId ? <Link className="btn-secondary min-h-[40px]" to={reportPath(customerId, a.reportId)}>{a.label}</Link> : null;
    default:
      return null;
  }
}

/**
 * "Privileged role assignments in Microsoft Entra, from today's collection at 6:02 AM. Reviewed every quarter. Next
 * campaign: Jan 11, 2027." Separate sentences, so a wrapped line never starts with punctuation. The schedule words open
 * Setup in place for whoever may change them (spec 13.1).
 */
function Subline({ data, customerId, canEdit, onSchedule, readOnly }) {
  const when = collectionPhrase(data.collectedAt) ?? 'the latest collection';
  const words = data.schedule?.words ?? 'every quarter';
  const off = data.schedule?.cadence === 'off';
  const scheduleText = off ? 'Not scheduled' : `Reviewed ${words}`;
  const next = !off && data.schedule?.nextStart ? ` Next campaign: ${longDate(data.schedule.nextStart)}.` : '';
  const schedule = canEdit
    ? <button type="button" className="tap font-semibold text-ipc-light underline decoration-ipc-field underline-offset-2 hover:decoration-ipc-light" onClick={onSchedule} data-testid="access-reviews-schedule">{scheduleText}</button>
    : <span>{scheduleText}</span>;
  if (data.stale) {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-2" data-testid="access-reviews-stale">
        <Dot tone="warn" />
        <span>From {when.replace(/^the collection of /, 'the collection on ')}. Today's collection has not arrived.</span>
        {!readOnly ? <Link className="font-semibold text-ipc-light hover:underline" to={customerSectionPath(customerId, 'data-collection')}>Open Data collection</Link> : null}
      </span>
    );
  }
  return (
    <span data-testid="access-reviews-subline">
      Privileged role assignments in Microsoft Entra, from {when}. {schedule}.{next}
    </span>
  );
}

function Kpis({ kpis, current, names }) {
  const from = kpis.fromClosed ? 'Last closed campaign' : current?.name ?? 'Current campaign';
  const pct = kpis.total > 0 ? Math.round((kpis.decided / kpis.total) * 100) : 0;
  const due = kpis.dueAt ? shortDate(kpis.dueAt) : '';
  return (
    <KpiGrid max={4}>
      <KpiCard label="Assignments to review" kpi="assignments" value={kpis.assignments} caption={`${plural(kpis.rolesInScope ?? 0, 'privileged role')} in scope, ${from}`} />
      <KpiCard label="Decided" kpi="decided" value={kpis.decided} unit={`of ${kpis.total}`} variant="bar" percent={pct} caption={kpis.fromClosed ? 'From the last closed campaign' : `Due ${due}`} />
      <KpiCard
        label="Removals pending verification" kpi="removals" value={kpis.removalsPending}
        caption={`${kpis.removalsDecided} decided, ${kpis.removalsVerified} verified removed by collection`}
      />
      <KpiCard
        label="Overdue" kpi="overdue" value={kpis.overdue}
        caption={kpis.overdue > 0 ? `No decision after ${due}; escalated to ${names ? 'you' : 'the administrators'}` : 'Nothing past the due date'}
      />
    </KpiGrid>
  );
}

/** Entitled, never ran (spec 13.3): what a review is, in one paragraph, and the two ways to begin. */
function NoCampaignYet({ data, can, onStart, onSchedule, busy, startLabel }) {
  const inScope = (data.roles ?? []).filter((r) => r.inScope).reduce((n, r) => n + (Number.isInteger(r.holders) ? r.holders : 0), 0);
  const plan = data.p2 ? 'Microsoft Entra ID P2' : 'Microsoft Entra ID P1';
  return (
    <SectionCard title="No access review yet" testId="access-reviews-empty">
      <p className="text-sm text-ipc-ink">
        Your {plan} tenant has {plural(inScope, 'privileged role assignment')} and no recorded review. A review asks a named
        person to keep or remove each one and gives you evidence for audits.
      </p>
      {can.edit ? (
        <div className="mt-4 flex flex-wrap gap-2.5">
          <button type="button" className="btn-primary min-h-[40px]" disabled={busy} onClick={onStart} data-testid="access-reviews-empty-start">{startLabel}</button>
          <button type="button" className="btn-secondary min-h-[40px]" onClick={onSchedule}>Schedule quarterly</button>
        </div>
      ) : null}
    </SectionCard>
  );
}

